import { useState, useEffect, useCallback, useMemo } from "react";
import UsuariosService from "../../../services/usuarios.service";
import { MOSTRADORES } from "../../../config/constants";
import { CLAVE_EMPLEADO, CLAVE_MOSTRADOR } from "../constants";
import { opcionesEmpleado } from "../helpers/entradas";

const MOSTRADOR_POR_DEFECTO = MOSTRADORES[0].value;

const leerAlmacenado = (clave) => {
    try {
        return localStorage.getItem(clave) || "";
    } catch (err) {
        return "";
    }
};

const guardarAlmacenado = (clave, valor) => {
    try {
        localStorage.setItem(clave, valor);
    } catch (err) {
        console.error("No se pudo guardar la preferencia:", clave, err);
    }
};

/**
 * Hook con la configuración del puesto del gestor: lista de empleados y la
 * selección persistente (localStorage, mismas claves que el legacy) de empleado
 * y mostrador. Un valor guardado que ya no es válido se trata como no
 * seleccionado, sin sobreescribir el almacenamiento hasta que el usuario elija.
 * @returns {{
 *   empleados: Array,
 *   empleadoId: string,
 *   mostrador: string,
 *   setEmpleadoId: (id: string) => void,
 *   setMostrador: (valor: string) => void,
 *   cargandoEmpleados: boolean
 * }} Empleados y selección vigente
 */
export const usePreferenciasGestor = () => {
    const [empleados, setEmpleados] = useState([]);
    const [cargandoEmpleados, setCargandoEmpleados] = useState(true);
    const [empleadoGuardado, setEmpleadoGuardado] = useState(() => leerAlmacenado(CLAVE_EMPLEADO));
    const [mostradorGuardado, setMostradorGuardado] = useState(() => leerAlmacenado(CLAVE_MOSTRADOR));

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const lista = await UsuariosService.listarUsuariosMap();
                if (!cancelled) setEmpleados(Array.isArray(lista) ? lista : []);
            } catch (err) {
                console.error("Error cargando empleados:", err);
                if (!cancelled) setEmpleados([]);
            } finally {
                if (!cancelled) setCargandoEmpleados(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const empleadoId = useMemo(() => {
        const existe = opcionesEmpleado(empleados).some((o) => o.value === empleadoGuardado);
        return existe ? empleadoGuardado : "";
    }, [empleados, empleadoGuardado]);

    const mostrador = MOSTRADORES.some((m) => m.value === mostradorGuardado)
        ? mostradorGuardado
        : MOSTRADOR_POR_DEFECTO;

    const setEmpleadoId = useCallback((id) => {
        setEmpleadoGuardado(id);
        guardarAlmacenado(CLAVE_EMPLEADO, id);
    }, []);

    const setMostrador = useCallback((valor) => {
        setMostradorGuardado(valor);
        guardarAlmacenado(CLAVE_MOSTRADOR, valor);
    }, []);

    return { empleados, empleadoId, mostrador, setEmpleadoId, setMostrador, cargandoEmpleados };
};
